import React, { useState } from 'react';
import { Alert, Form, Modal } from 'react-bootstrap';

import Button from '../../../common/components/Button';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { useNavigation } from '../../../common/navigation';
import { FormRow210 } from '../../../app/components/FormRow';
import momencik, {
  prevMonthDateFrom,
  prevMonthDateTo,
} from '../../../common/momencik';
import CompanyByNameOrNipSelector from '../Company/CompanyByNameOrNipSelector';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import { ResponseError } from '../../../common/spdCore/autogenerated/spdApiClient';

interface GenerateAutoInvoiceModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
}

export const GenerateAutoInvoiceModal: React.FC<
  GenerateAutoInvoiceModalProps
> = ({ show, onHide, onSuccess }) => {
  const spd = useSpdCore();
  const nav = useNavigation();
  const [companyId, setCompanyId] = useState<string | undefined>();
  const [dateFrom, setDateFrom] = useState(prevMonthDateFrom());
  const [dateTo, setDateTo] = useState(prevMonthDateTo());
  const [loading, setLoading] = useState(false);
  const [alertMsg, setAlertMsg] = useState<string | undefined>();
  const [errorMsg, setErrorMsg] = useState<string | undefined>();

  const handleHide = () => {
    setCompanyId(undefined);
    setDateFrom(prevMonthDateFrom());
    setDateTo(prevMonthDateTo());
    setAlertMsg(undefined);
    setErrorMsg(undefined);
    onHide();
  };

  const handleSubmit = async () => {
    setAlertMsg(undefined);
    setErrorMsg(undefined);
    if (!dateFrom) {
      setErrorMsg('Podaj datę od');
      return;
    }
    if (!dateTo) {
      setErrorMsg('Podaj datę do');
      return;
    }
    setLoading(true);
    try {
      const result = await spd.salesInvoices.generateAutoInvoice(
        companyId!,
        dateFrom,
        dateTo,
      );
      if (result.outcome === 'approved' || result.outcome === 'draft') {
        onSuccess();
        handleHide();
        nav.salesInvoiceEditor(result.invoiceId);
      } else {
        setAlertMsg('Brak nierozliczonych badań w podanym zakresie dat');
      }
    } catch (err) {
      const errors = await responseErrors(err as ResponseError);
      setErrorMsg(errors.join('\n'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleHide} backdrop="static" size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Dodaj automatyczną fakturę</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info" className="mb-3">
          Wygeneruje fakturę automatyczną dla wybranej firmy za badania
          zakończone w podanym zakresie dat, które nie zostały jeszcze
          rozliczone.
        </Alert>
        <Form>
          <FormRow210 controlId="company" label="Firma" className="mb-3">
            <CompanyByNameOrNipSelector
              value={companyId}
              clearable={true}
              onChange={option =>
                setCompanyId(option?.value as string | undefined)
              }
              placeholder="Wskaż firmę..."
            />
          </FormRow210>
          <FormRow210 controlId="dateFrom" label="Data od" className="mb-3">
            <Form.Control
              type="date"
              required
              value={momencik(new Date(dateFrom), 'YYYY-MM-DD')}
              onChange={e => setDateFrom(e.target.value)}
            />
          </FormRow210>
          <FormRow210 controlId="dateTo" label="Data do" className="mb-3">
            <Form.Control
              type="date"
              required
              value={momencik(new Date(dateTo), 'YYYY-MM-DD')}
              onChange={e => setDateTo(e.target.value)}
            />
          </FormRow210>
          {alertMsg && <Alert variant="info">{alertMsg}</Alert>}
          {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="outline-primary"
          onClick={handleSubmit}
          disabled={!companyId}
          isLoading={loading}
        >
          Generuj fakturę
        </Button>
        <Button
          variant="outline-secondary"
          onClick={handleHide}
          disabled={loading}
        >
          Anuluj
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
